import React, { useEffect } from 'react';

/**
 * PrintReadyMarker
 * =================
 * Invisible DOM sentinel that lets the backend's Playwright PDF renderer know
 * the page has fully hydrated. Drop this into every route that can be exported
 * to PDF via `POST /api/print/pdf`.
 *
 * Pattern for any new printable route (single line):
 *
 *     <PrintReadyMarker ready={!loading && dataReady} />
 *
 * ...or, for routes with heavy imagery you want captured cleanly:
 *
 *     <PrintReadyMarker ready={!loading && dataReady} waitForImages />
 *
 * How it works
 * ------------
 * • Renders `<div data-print-ready="true" />` (visually hidden) once `ready`
 *   flips true. Optionally waits for every currently-in-DOM `<img>` to
 *   finish decoding first (8s hard cap so a slow CDN can't hang the render).
 * • Backend `routes/print_pdf.py :: _render_pdf` runs
 *   `page.wait_for_selector('[data-print-ready="true"]', timeout=15000)`
 *   after `domcontentloaded`, then snapshots. Fallback = fixed 2500ms wait.
 * • The backend keeps an allowlist `_EXPECTED_UNMARKED_ROUTES` of pages that
 *   intentionally don't emit the sentinel (library, dashboard, tour, geography,
 *   maps, settings). Add your route there if it's legitimately unmarked to
 *   silence the "No sentinel" WARNING in prod logs.
 *
 * Boundary
 * --------
 * `waitForImages` snapshots img nodes at mount time; images added later
 * (lazy-load, later async content) are NOT waited on. The backend's 300ms
 * post-selector buffer + Playwright's own frame commit gives late-mount
 * children a chance to appear before capture, but if you have a truly
 * async-heavy route, flip `ready` only after all your async data is settled.
 */
export default function PrintReadyMarker({ ready = false, waitForImages = false }) {
  const [imagesDone, setImagesDone] = React.useState(!waitForImages);

  useEffect(() => {
    if (!waitForImages || !ready) return;
    // Snapshot all currently-present top-level images and wait for each to
    // finish decoding. Fresh images added after this snapshot are covered
    // by the 2.5s hydrate buffer on the render side.
    const imgs = Array.from(document.querySelectorAll('img'));
    if (imgs.length === 0) {
      setImagesDone(true);
      return;
    }
    const settle = () => setImagesDone(true);
    let pending = imgs.length;
    const onOne = () => {
      pending -= 1;
      if (pending <= 0) settle();
    };
    imgs.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        onOne();
      } else {
        img.addEventListener('load', onOne, { once: true });
        img.addEventListener('error', onOne, { once: true });
      }
    });
    // Hard cap: even if some images never resolve, unblock the render after 8s
    const cap = setTimeout(settle, 8000);
    return () => clearTimeout(cap);
  }, [ready, waitForImages]);

  if (!ready || !imagesDone) return null;

  return (
    <div
      data-print-ready="true"
      aria-hidden="true"
      style={{
        position: 'absolute',
        width: 1,
        height: 1,
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        whiteSpace: 'nowrap',
      }}
    />
  );
}
